/**
 * Automation scheduling: when a rule next fires, whether it is a rule at all,
 * and how to say it.
 *
 * It lives in `@repo/shared` because every side needs it and they must agree:
 * the API validates a schedule and computes `nextRunAt` from it, the runners
 * advance it after each run, the workspace's agent tools check it before the
 * API sees it, and the web page describes a draft before it is saved. Copies
 * would drift into a page that promises a time the runner does not keep.
 *
 * The rules, borrowed in shape from t3code's scheduled tasks (interval +
 * fixed time with a weekday mask) and widened for a compliance calendar:
 *
 *  - `once`      — one date and time; the automation finishes after it runs.
 *  - `interval`  — every N minutes, restarting each day at `time`, optionally
 *                  only until a closing time and only on chosen weekdays.
 *  - `daily`     — every day at `time`.
 *  - `weekdays`  — Monday to Friday at `time`.
 *  - `weekly`    — on each chosen weekday at `time`.
 *  - `monthly`   — on one day of the month; a day the month lacks (31 in
 *                  April) runs on its last day instead of being skipped.
 *
 * Recurring rules may also carry a `startDate` and an `endDate` (inclusive,
 * in the schedule's zone). A rule with no occurrence left is finished:
 * {@link computeNextRunAt} returns `null`, and the runners turn it off.
 *
 * Timezone-aware without a date library: all arithmetic happens on a UTC-noon
 * anchor for the target zone's calendar day, so a run at 09:00 stays 09:00
 * across DST rather than sliding by an hour.
 *
 * @module automationSchedule
 */
export type AutomationFrequency =
  | "once"
  | "interval"
  | "daily"
  | "weekdays"
  | "weekly"
  | "monthly";

export type AutomationSchedule = {
  frequency: AutomationFrequency;
  /**
   * "HH:MM" on the zone's wall clock. For `interval`, the time each day's
   * runs start from.
   */
  time: string;
  /** IANA zone the times and dates are read in, e.g. "Europe/London". */
  timezone: string;
  /** `once`: the day it runs, "YYYY-MM-DD". */
  date?: string;
  /**
   * `once`, input only: "in N minutes". {@link normalizeSchedule} turns it
   * into the `date` and `time` it means on the zone's clock, so an agent that
   * knows the date but not the minute can still say "in 20 minutes" exactly.
   */
  afterMinutes?: number;
  /** `interval`: minutes between runs. */
  everyMinutes?: number;
  /** `interval`: the latest a run may start each day, "HH:MM" (default 23:59). */
  until?: string;
  /** `weekly` (and optionally `interval`): 0 = Sunday … 6 = Saturday. */
  weekdays?: number[];
  /** Legacy `weekly` field — one day. Read when `weekdays` is absent. */
  weekday?: number;
  /** `monthly`: 1–31; past the month's end means its last day. */
  dayOfMonth?: number;
  /** Recurring rules: the first day a run may happen, "YYYY-MM-DD". */
  startDate?: string;
  /** Recurring rules: the last day a run may happen, "YYYY-MM-DD". */
  endDate?: string;
};

export const AUTOMATION_FREQUENCIES: AutomationFrequency[] = [
  "once",
  "interval",
  "daily",
  "weekdays",
  "weekly",
  "monthly",
];

/** Shortest gap between interval runs. Each run is a full agent turn. */
export const MIN_INTERVAL_MINUTES = 15;
/** Longest interval: beyond a day, a daily or weekly rule says it better. */
export const MAX_INTERVAL_MINUTES = 24 * 60;

export const WEEKDAY_LABELS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
] as const;

const WEEKDAY_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;
const MONTH_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

const TIME_PATTERN = /^([01]\d|2[0-3]):([0-5]\d)$/;
const DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const DAY_MS = 24 * 60 * 60 * 1000;
/** Monthly needs two months of candidates; a start date can be further out. */
const MAX_SEARCH_DAYS = 62;

export function isValidScheduleTime(time: string): boolean {
  return TIME_PATTERN.test(time);
}

export function isValidTimezone(timezone: string): boolean {
  if (timezone.trim().length === 0) return false;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: timezone });
    return true;
  } catch {
    return false;
  }
}

/** A real calendar date in "YYYY-MM-DD" (so 2026-02-30 is refused). */
export function isValidScheduleDate(date: string): boolean {
  const match = DATE_PATTERN.exec(date);
  if (!match) return false;
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const probe = new Date(Date.UTC(year, month - 1, day));
  return (
    probe.getUTCFullYear() === year && probe.getUTCMonth() === month - 1 && probe.getUTCDate() === day
  );
}

function minutesOf(time: string): number {
  const match = TIME_PATTERN.exec(time);
  if (!match) throw new Error(`Invalid schedule time: ${time}`);
  return Number(match[1]) * 60 + Number(match[2]);
}

/** Milliseconds the zone's wall clock is ahead of UTC at the given instant. */
function timezoneOffsetMs(timestamp: number, timeZone: string): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(new Date(timestamp));

  const get = (type: string) => Number(parts.find((part) => part.type === type)?.value ?? "0");

  const wallClockUtc = Date.UTC(
    get("year"),
    get("month") - 1,
    get("day"),
    // Intl emits hour "24" for midnight in some environments.
    get("hour") % 24,
    get("minute"),
    get("second"),
  );
  return wallClockUtc - timestamp;
}

/**
 * UTC instant at which the zone's wall clock shows the given date + time.
 *
 * In the hour the clocks go back a wall time happens twice; the earlier one
 * is meant — "01:50" on that night is the first 01:50, not an hour later. In
 * the hour the clocks go forward it does not happen at all; the instant the
 * standard offset gives (a little after the jump) is returned, and
 * {@link normalizeSchedule} refuses such a one-off outright.
 */
function zonedTimeToUtc(
  year: number,
  monthIndex: number,
  day: number,
  minuteOfDay: number,
  timeZone: string,
): Date {
  const utcGuess = Date.UTC(year, monthIndex, day, Math.floor(minuteOfDay / 60), minuteOfDay % 60);
  // The zone's offsets either side of the date cover both readings of an
  // ambiguous wall time; each candidate is kept only if it round-trips.
  const offsets = new Set([
    timezoneOffsetMs(utcGuess - DAY_MS, timeZone),
    timezoneOffsetMs(utcGuess, timeZone),
    timezoneOffsetMs(utcGuess + DAY_MS, timeZone),
  ]);
  let earliest: number | null = null;
  for (const offset of offsets) {
    const candidate = utcGuess - offset;
    if (timezoneOffsetMs(candidate, timeZone) !== offset) continue;
    if (earliest === null || candidate < earliest) earliest = candidate;
  }
  if (earliest !== null) return new Date(earliest);
  // A wall time the zone skips: one refinement pass, as before.
  const offset = timezoneOffsetMs(utcGuess, timeZone);
  let timestamp = utcGuess - offset;
  const refinedOffset = timezoneOffsetMs(timestamp, timeZone);
  if (refinedOffset !== offset) timestamp = utcGuess - refinedOffset;
  return new Date(timestamp);
}

/** Whether the zone's wall clock ever shows this date + time (not in a spring-forward gap). */
function wallTimeExists(date: string, time: string, timeZone: string): boolean {
  const day = new Date(dateAnchor(date));
  const at = zonedTimeToUtc(day.getUTCFullYear(), day.getUTCMonth(), day.getUTCDate(), minutesOf(time), timeZone);
  const wall = new Date(at.getTime() + timezoneOffsetMs(at.getTime(), timeZone)).toISOString();
  return wall.slice(0, 10) === date && wall.slice(11, 16) === time;
}

/** The zone's calendar day at `instant`, as a UTC-noon anchor. */
function zonedDayAnchor(instant: Date, timeZone: string): number {
  const wallClock = new Date(instant.getTime() + timezoneOffsetMs(instant.getTime(), timeZone));
  return Date.UTC(wallClock.getUTCFullYear(), wallClock.getUTCMonth(), wallClock.getUTCDate(), 12);
}

function dateAnchor(date: string): number {
  const match = DATE_PATTERN.exec(date);
  if (!match) throw new Error(`Invalid schedule date: ${date}`);
  return Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12);
}

function anchorKey(anchor: number): string {
  return new Date(anchor).toISOString().slice(0, 10);
}

/** The zone's calendar date at `instant`, "YYYY-MM-DD". */
export function zonedDateKey(instant: Date, timeZone: string): string {
  return anchorKey(zonedDayAnchor(instant, isValidTimezone(timeZone) ? timeZone : "UTC"));
}

function daysInMonth(year: number, monthIndex: number): number {
  return new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate();
}

/** The weekdays a weekly (or day-limited interval) rule runs on; null = every day. */
function scheduleWeekdays(schedule: AutomationSchedule): ReadonlySet<number> | null {
  if (schedule.weekdays && schedule.weekdays.length > 0) {
    return new Set(schedule.weekdays);
  }
  if (schedule.frequency === "weekly") {
    return new Set([schedule.weekday ?? 1]);
  }
  return null;
}

function matchesDay(schedule: AutomationSchedule, candidate: Date): boolean {
  const weekday = candidate.getUTCDay();
  switch (schedule.frequency) {
    case "daily":
      return true;
    case "weekdays":
      return weekday >= 1 && weekday <= 5;
    case "weekly":
    case "interval": {
      const days = scheduleWeekdays(schedule);
      return days === null || days.has(weekday);
    }
    case "monthly": {
      const wanted = schedule.dayOfMonth ?? 1;
      const last = daysInMonth(candidate.getUTCFullYear(), candidate.getUTCMonth());
      return candidate.getUTCDate() === Math.min(wanted, last);
    }
    default:
      return false;
  }
}

/** The minutes of the day a matching day runs at, ascending. */
function slotsOf(schedule: AutomationSchedule): number[] {
  const start = minutesOf(schedule.time);
  if (schedule.frequency !== "interval") return [start];
  const every = Math.max(MIN_INTERVAL_MINUTES, schedule.everyMinutes ?? 60);
  const end = schedule.until ? minutesOf(schedule.until) : 24 * 60 - 1;
  const slots: number[] = [];
  for (let minute = start; minute <= end; minute += every) slots.push(minute);
  return slots;
}

/**
 * Next occurrence of the schedule strictly after `after`, in the schedule's
 * timezone (including DST shifts) — or `null` when the rule has none left: a
 * `once` whose moment has passed, or a rule past its `endDate`.
 */
export function computeNextRunAt(schedule: AutomationSchedule, after: Date = new Date()): Date | null {
  const timeZone = isValidTimezone(schedule.timezone) ? schedule.timezone : "UTC";

  if (schedule.frequency === "once") {
    if (!schedule.date) throw new Error("A one-off schedule needs a date");
    const day = new Date(dateAnchor(schedule.date));
    const occurrence = zonedTimeToUtc(
      day.getUTCFullYear(),
      day.getUTCMonth(),
      day.getUTCDate(),
      minutesOf(schedule.time),
      timeZone,
    );
    return occurrence.getTime() > after.getTime() ? occurrence : null;
  }

  const slots = slotsOf(schedule);
  const endAnchor = schedule.endDate ? dateAnchor(schedule.endDate) : null;
  // Calendar date of `after` in the target zone, carried as a UTC-noon anchor
  // so day arithmetic can't slip across midnight — or the start date, when
  // that is later.
  let anchor = zonedDayAnchor(after, timeZone);
  if (schedule.startDate) anchor = Math.max(anchor, dateAnchor(schedule.startDate));

  for (let dayOffset = 0; dayOffset <= MAX_SEARCH_DAYS; dayOffset++) {
    const dayAnchor = anchor + dayOffset * DAY_MS;
    if (endAnchor !== null && dayAnchor > endAnchor) return null;
    const candidateDay = new Date(dayAnchor);
    if (!matchesDay(schedule, candidateDay)) continue;
    for (const minute of slots) {
      const occurrence = zonedTimeToUtc(
        candidateDay.getUTCFullYear(),
        candidateDay.getUTCMonth(),
        candidateDay.getUTCDate(),
        minute,
        timeZone,
      );
      if (occurrence.getTime() > after.getTime()) return occurrence;
    }
  }

  // Every valid rule repeats within two months; only a malformed one lands here.
  throw new Error("No upcoming occurrence found for schedule");
}

const isIntegerIn = (value: unknown, min: number, max: number): value is number =>
  typeof value === "number" && Number.isInteger(value) && value >= min && value <= max;

/**
 * The schedule in its canonical stored form, or the one sentence that says
 * why it is not a schedule. Fields a rule does not read are dropped, so a
 * monthly schedule never carries a stale weekday from an earlier edit, and a
 * weekly one always stores `weekdays` (sorted, unique).
 *
 * With `now`, a rule must also have an occurrence after it: a one-off in the
 * past, or an end date already gone, is refused rather than saved to never
 * run.
 */
export function normalizeSchedule(
  input: AutomationSchedule,
  now?: Date,
): { readonly schedule: AutomationSchedule } | { readonly error: string } {
  const time = (input.time ?? "").trim();
  const relative = input.frequency === "once" && input.afterMinutes !== undefined;
  if (!relative && !TIME_PATTERN.test(time)) {
    return { error: `Schedule time must be 24-hour HH:MM (got "${input.time}").` };
  }
  const timezone = (input.timezone ?? "").trim();
  if (!isValidTimezone(timezone)) {
    return {
      error: `Schedule timezone must be an IANA zone such as "Europe/London" (got "${input.timezone}").`,
    };
  }

  const recurring = (
    schedule: AutomationSchedule,
  ): { readonly schedule: AutomationSchedule } | { readonly error: string } => {
    const startDate = input.startDate?.trim() || undefined;
    const endDate = input.endDate?.trim() || undefined;
    if (startDate !== undefined && !isValidScheduleDate(startDate)) {
      return { error: `startDate must be a real date as YYYY-MM-DD (got "${input.startDate}").` };
    }
    if (endDate !== undefined && !isValidScheduleDate(endDate)) {
      return { error: `endDate must be a real date as YYYY-MM-DD (got "${input.endDate}").` };
    }
    if (startDate !== undefined && endDate !== undefined && endDate < startDate) {
      return { error: "endDate is before startDate, so the schedule would never run." };
    }
    return {
      schedule: {
        ...schedule,
        ...(startDate !== undefined ? { startDate } : {}),
        ...(endDate !== undefined ? { endDate } : {}),
      },
    };
  };

  let result: { readonly schedule: AutomationSchedule } | { readonly error: string };
  switch (input.frequency) {
    case "once": {
      if (input.afterMinutes !== undefined) {
        if (!isIntegerIn(input.afterMinutes, 1, 366 * 24 * 60)) {
          return { error: `afterMinutes must be a whole number of minutes from now (got ${String(input.afterMinutes)}).` };
        }
        if (now === undefined) {
          return { error: "afterMinutes needs the current time to be known." };
        }
        // Rounded up to the whole minute a run can start at.
        const at = new Date(Math.ceil((now.getTime() + input.afterMinutes * 60_000) / 60_000) * 60_000);
        const wall = new Date(at.getTime() + timezoneOffsetMs(at.getTime(), timezone));
        const schedule: AutomationSchedule = {
          frequency: "once",
          date: wall.toISOString().slice(0, 10),
          time: wall.toISOString().slice(11, 16),
          timezone,
        };
        // In the hour the clocks go back a wall-clock time happens twice, and
        // the stored date and time would name the other one — an hour out.
        // Refuse rather than guess; an exact time says which is meant.
        if (computeNextRunAt(schedule, new Date(at.getTime() - 1))?.getTime() !== at.getTime()) {
          return {
            error: `In ${afterMinutesLabel(input.afterMinutes)} it is the hour the clocks go back in ${timezone}, so that time happens twice. Give the exact date and time instead.`,
          };
        }
        return { schedule };
      }
      const date = input.date?.trim() ?? "";
      if (!isValidScheduleDate(date)) {
        return {
          error: `A one-off schedule needs the date it runs as YYYY-MM-DD (got "${input.date ?? ""}").`,
        };
      }
      if (!wallTimeExists(date, time, timezone)) {
        return {
          error: `${date} ${time} does not exist in ${timezone} — the clocks go forward over it. Pick a time outside that hour.`,
        };
      }
      result = { schedule: { frequency: "once", date, time, timezone } };
      break;
    }
    case "interval": {
      if (!isIntegerIn(input.everyMinutes, MIN_INTERVAL_MINUTES, MAX_INTERVAL_MINUTES)) {
        return {
          error: `An interval schedule needs everyMinutes, a whole number from ${MIN_INTERVAL_MINUTES} to ${MAX_INTERVAL_MINUTES} (got ${String(input.everyMinutes)}).`,
        };
      }
      const until = input.until?.trim() || undefined;
      if (until !== undefined) {
        if (!TIME_PATTERN.test(until)) {
          return { error: `until must be 24-hour HH:MM (got "${input.until}").` };
        }
        if (minutesOf(until) < minutesOf(time)) {
          return { error: `until (${until}) is before the start time (${time}); runs cannot cross midnight.` };
        }
      }
      const days = input.weekdays === undefined ? undefined : normalizeWeekdays(input.weekdays);
      if (days !== undefined && "error" in days) return days;
      result = recurring({
        frequency: "interval",
        time,
        timezone,
        everyMinutes: input.everyMinutes,
        ...(until !== undefined ? { until } : {}),
        ...(days !== undefined && days.weekdays.length < 7 ? { weekdays: days.weekdays } : {}),
      });
      break;
    }
    case "weekly": {
      // `weekday` is the legacy field, read only when `weekdays` is absent: an
      // emptied `weekdays` is a rule with no days, not a cue to fall back.
      const raw =
        input.weekdays !== undefined
          ? input.weekdays
          : input.weekday !== undefined
            ? [input.weekday]
            : [];
      if (raw.length === 0) {
        return { error: "A weekly schedule needs weekdays: 0 = Sunday … 6 = Saturday, e.g. [1, 3, 5]." };
      }
      const days = normalizeWeekdays(raw);
      if ("error" in days) return days;
      result = recurring({ frequency: "weekly", time, timezone, weekdays: days.weekdays });
      break;
    }
    case "monthly": {
      if (!isIntegerIn(input.dayOfMonth, 1, 31)) {
        return {
          error:
            "A monthly schedule needs a dayOfMonth from 1 to 31; days a month lacks run on its last day (31 = always the last day).",
        };
      }
      result = recurring({ frequency: "monthly", time, timezone, dayOfMonth: input.dayOfMonth });
      break;
    }
    case "daily":
    case "weekdays":
      result = recurring({ frequency: input.frequency, time, timezone });
      break;
    default:
      return { error: `Unknown frequency "${String(input.frequency)}".` };
  }

  if ("error" in result || now === undefined) return result;
  if (computeNextRunAt(result.schedule, now) === null) {
    return {
      error:
        result.schedule.frequency === "once"
          ? `${result.schedule.date} ${result.schedule.time} (${timezone}) has already passed. Pick a time in the future.`
          : `The schedule ends on ${result.schedule.endDate}, so it has no run left. Pick a later end date.`,
    };
  }
  return result;
}

function afterMinutesLabel(minutes: number): string {
  return minutes === 1 ? "1 minute" : `${minutes} minutes`;
}

function normalizeWeekdays(
  weekdays: ReadonlyArray<number>,
): { readonly weekdays: number[] } | { readonly error: string } {
  if (weekdays.length === 0 || !weekdays.every((day) => isIntegerIn(day, 0, 6))) {
    return { error: "weekdays must list days as 0 = Sunday … 6 = Saturday, e.g. [1, 3, 5]." };
  }
  return { weekdays: [...new Set(weekdays)].sort((a, b) => a - b) };
}

function formatTime12h(time: string): string {
  const match = TIME_PATTERN.exec(time);
  if (!match) {
    return time;
  }
  const hour = Number(match[1]);
  const minute = match[2];
  const period = hour >= 12 ? "PM" : "AM";
  const displayHour = hour % 12 === 0 ? 12 : hour % 12;
  return `${displayHour}:${minute} ${period}`;
}

function ordinal(day: number): string {
  const remainder = day % 10;
  if (day >= 11 && day <= 13) {
    return `${day}th`;
  }
  if (remainder === 1) {
    return `${day}st`;
  }
  if (remainder === 2) {
    return `${day}nd`;
  }
  if (remainder === 3) {
    return `${day}rd`;
  }
  return `${day}th`;
}

/** "Fri 25 Sep 2026" for a "YYYY-MM-DD" date. */
function formatDate(date: string): string {
  if (!isValidScheduleDate(date)) return date;
  const day = new Date(dateAnchor(date));
  return `${WEEKDAY_SHORT[day.getUTCDay()]} ${day.getUTCDate()} ${MONTH_SHORT[day.getUTCMonth()]} ${day.getUTCFullYear()}`;
}

function joinList(items: ReadonlyArray<string>): string {
  if (items.length <= 1) return items.join("");
  return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
}

/** "Monday", "weekdays", "Mon, Wed and Fri". */
function describeDays(days: ReadonlySet<number>): string {
  const sorted = [...days].sort((a, b) => a - b);
  if (sorted.length === 5 && sorted.every((day) => day >= 1 && day <= 5)) return "weekdays";
  if (sorted.length === 2 && sorted[0] === 0 && sorted[1] === 6) return "weekends";
  if (sorted.length === 1) return WEEKDAY_LABELS[sorted[0]!]!;
  return joinList(sorted.map((day) => WEEKDAY_SHORT[day]!));
}

function describeEvery(minutes: number): string {
  if (minutes % 60 === 0) {
    const hours = minutes / 60;
    return hours === 1 ? "Every hour" : `Every ${hours} hours`;
  }
  return `Every ${minutes} minutes`;
}

/**
 * "Once on Fri 25 Sep 2026 at 3:44 PM", "Every 2 hours, 9:00 AM–5:00 PM on
 * weekdays", "Weekly on Mon, Wed and Fri at 9:00 AM", "Monthly on the 31st
 * (or the month's last day) at 9:00 AM, until Wed 31 Dec 2026".
 */
export function describeSchedule(schedule: AutomationSchedule): string {
  const time = formatTime12h(schedule.time);
  let sentence: string;
  switch (schedule.frequency) {
    case "once":
      return `Once on ${formatDate(schedule.date ?? "")} at ${time}`;
    case "interval": {
      const every = describeEvery(schedule.everyMinutes ?? 60);
      const window =
        schedule.until !== undefined
          ? `, ${time}–${formatTime12h(schedule.until)}`
          : schedule.time !== "00:00"
            ? `, from ${time}`
            : "";
      const days = scheduleWeekdays(schedule);
      sentence = `${every}${window}${days === null ? "" : ` on ${describeDays(days)}`}`;
      break;
    }
    case "daily":
      sentence = `Daily at ${time}`;
      break;
    case "weekdays":
      sentence = `Weekdays at ${time}`;
      break;
    case "weekly":
      sentence = `Weekly on ${describeDays(scheduleWeekdays(schedule) ?? new Set([1]))} at ${time}`;
      break;
    case "monthly": {
      const day = schedule.dayOfMonth ?? 1;
      sentence = `Monthly on the ${ordinal(day)}${day > 28 ? " (or the month's last day)" : ""} at ${time}`;
      break;
    }
    default:
      return time;
  }
  if (schedule.startDate) sentence += `, from ${formatDate(schedule.startDate)}`;
  if (schedule.endDate) sentence += `, until ${formatDate(schedule.endDate)}`;
  return sentence;
}

/**
 * An instant on the schedule's own wall clock: "Thu 25 Sep 2026, 15:44
 * (Europe/London)". What an agent reads back to confirm a time, so it can say
 * exactly when something will happen rather than repeat a UTC timestamp.
 */
export function formatInTimezone(instant: Date, timeZone: string): string {
  const zone = isValidTimezone(timeZone) ? timeZone : "UTC";
  const wall = new Date(instant.getTime() + timezoneOffsetMs(instant.getTime(), zone));
  const hh = String(wall.getUTCHours()).padStart(2, "0");
  const mm = String(wall.getUTCMinutes()).padStart(2, "0");
  return `${WEEKDAY_SHORT[wall.getUTCDay()]} ${wall.getUTCDate()} ${MONTH_SHORT[wall.getUTCMonth()]} ${wall.getUTCFullYear()}, ${hh}:${mm} (${zone})`;
}

/** "in 14 hours", "in 3 days", "in 25 minutes" — for the list's next-run line. */
export function describeTimeUntil(target: Date, now: Date = new Date()): string {
  const deltaMs = target.getTime() - now.getTime();
  if (deltaMs <= 60 * 1000) {
    return "in under a minute";
  }
  const minutes = Math.round(deltaMs / (60 * 1000));
  if (minutes < 60) {
    return `in ${minutes} minute${minutes === 1 ? "" : "s"}`;
  }
  const hours = Math.round(minutes / 60);
  if (hours < 48) {
    return `in ${hours} hour${hours === 1 ? "" : "s"}`;
  }
  const days = Math.round(hours / 24);
  return `in ${days} days`;
}
